import { Emdash } from "~/components/Punctuation";
import { ColumnMetadata, ColumnTypes } from "../../../../components/DataGrid";
import { PlatformTableData, PlatformTableDataColumns } from "../..";
import { ImageLoader } from "~/components/ImageLoader";
import { DISTRIBUTION_TYPE_TOOLTIPS, RWA_COIN_ICON } from "~/consts";
import { DataGridDollarPercentChangeComboCell } from "~/components/DataGrid/DataGridDollarPercentChangeComboCell";
import { DataGridNumberPercentChangeComboCell } from "~/components/DataGrid/DataGridNumberPercentChangeComboCell";
import { getPlatformURL, getNetworkURL } from "~/routes";
import { Link } from "~/components/Link";
import { PlatformUserEvents } from "~/modules/analytics/events/platforms";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { Icon } from "~/components/Icon";
import { getBadgeClassName } from "~/components/Badge/component";
import { NetworkBadge } from "~/modules/assets/components/AssetTable/NetworkBadge";
import { Tooltip } from "~/components/Tooltip";
import { BroadridgeAssetCountCell } from "./BroadridgeAssetCountCell";
import * as React from "react";

// // // //

export const RANK_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: "rank",
    label: "#",
    type: ColumnTypes.number,
    width: 60,
    maxWidth: 60,
    cellValue: (platform) => {
        return platform.rank;
    },
};

export const NAME_INDIVIDUAL_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.name,
    label: "Name",
    type: ColumnTypes.name,
    width: 175,
    sortsOn: PlatformTableDataColumns.name,
    render: (platform) => {
        return (
            <Link
                href={getPlatformURL({ platformSlug: platform.slug })}
                analyticsLabel={PlatformUserEvents.TableLinkClick}
                variant="default"
                className="flex items-center gap-2"
            >
                <ImageLoader
                    src={platform.icon_url || RWA_COIN_ICON}
                    alt={`${platform.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-4 w-4 min-w-4 rounded-full"
                />
                <span className="dg-overflow">{platform.name}</span>
            </Link>
        );
    },
};

export const RWA_ASSET_COUNT_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.rwaAssetCount,
    label: "RWA Count",
    type: ColumnTypes.number,
    width: 100,
    maxWidth: 130,
    sortsOn: PlatformTableDataColumns.rwaAssetCount,
    render: (platform) => {
        if (platform.name.toLocaleLowerCase().includes("broadridge")) {
            return <BroadridgeAssetCountCell />;
        }

        if (platform.rwa_asset_count === null) {
            return <Emdash />;
        }

        return <>{platform.rwa_asset_count.toLocaleString()}</>;
    },
};

export const STABLECOIN_ASSET_COUNT_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.stablecoinAssetCount,
    label: "Stablecoin Asset Count",
    type: ColumnTypes.number,
    width: 130,
    maxWidth: 130,
    sortsOn: PlatformTableDataColumns.stablecoinAssetCount,
    cellValue: (platform) => {
        return platform.stablecoin_asset_count;
    },
};

export const DISTRIBUTED_MARKET_CAP_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.distributedMarketCap,
    label: (
        <>
            <p>RWA Total Value</p>
            <p className="text-xs">(Excl. Stablecoins)</p>
        </>
    ),
    type: ColumnTypes.dollar,
    sortsOn: PlatformTableDataColumns.distributedMarketCap,
    tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
    width: 175,
    render: (platform) => {
        const measure = platform.distributed_market_cap;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const REPRESENTED_MARKET_CAP_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.representedMarketCap,
    label: (
        <>
            <p>RWA Total Value</p>
            <p className="text-xs">(Excl. Stablecoins)</p>
        </>
    ),
    type: ColumnTypes.dollar,
    sortsOn: PlatformTableDataColumns.representedMarketCap,
    tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
    width: 175,
    render: (platform) => {
        const measure = platform.represented_market_cap;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const STABLECOIN_MARKET_CAP_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.stablecoinMarketCap,
    label: "Stablecoins Market Cap",
    type: ColumnTypes.dollar,
    sortsOn: PlatformTableDataColumns.stablecoinMarketCap,
    width: 175,
    render: (platform) => {
        const measure = platform.stablecoin_market_cap;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const RWA_HOLDERS_COUNT_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.rwaHoldersCount,
    label: "RWA Holders",
    tooltip: "Excludes Stablecoins",
    type: ColumnTypes.number,
    sortsOn: PlatformTableDataColumns.rwaHoldersCount,
    width: 150,
    render: (platform) => {
        const measure = platform.rwa_holders_count;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridNumberPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const STABLECOIN_HOLDERS_COUNT_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: PlatformTableDataColumns.stablecoinHoldersCount,
    label: "Stablecoin Holders",
    type: ColumnTypes.number,
    sortsOn: PlatformTableDataColumns.stablecoinHoldersCount,
    width: 150,
    render: (platform) => {
        const measure = platform.stablecoin_holders_count;

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridNumberPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const ASSET_CLASSES_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: "asset_classes",
    label: "Offered Asset Classes",
    type: ColumnTypes.string,
    width: 250,
    render: (platform) => {
        const stats = platform.asset_class_stats;

        if (stats.length === 0) {
            return <Emdash />;
        }

        const metadata = stats
            .sort((a, b) => {
                return (
                    (b.bridged_token_value_dollar?.val ?? 0) -
                    (a.bridged_token_value_dollar?.val ?? 0)
                );
            })
            .map((stat) => {
                return getAssetClassLinkMetadata(stat.id);
            });

        return (
            <div className="flex items-center gap-2">
                {metadata.slice(0, 2).map((meta) => {
                    if (meta.href === null) {
                        return (
                            <div
                                key={meta.label}
                                className={getBadgeClassName({
                                    size: "sm",
                                    variant: "gray",
                                    hover: false,
                                    rounded: "sm",
                                    className: "flex items-center gap-2",
                                })}
                            >
                                {meta.icon && (
                                    <Icon className="h-3" icon={meta.icon} />
                                )}
                                {meta.label}
                            </div>
                        );
                    }
                    return (
                        <Link
                            key={meta.label}
                            href={meta.href}
                            analyticsLabel={
                                PlatformUserEvents.ClickAssetClassLink
                            }
                            analyticsEventProperties={{
                                asset_class: meta.label,
                            }}
                            className={getBadgeClassName({
                                size: "sm",
                                variant: "gray",
                                hover: true,
                                rounded: "sm",
                                className: "flex items-center gap-2",
                            })}
                        >
                            {meta.icon && (
                                <Icon className="h-3" icon={meta.icon} />
                            )}
                            {meta.label}
                        </Link>
                    );
                })}
                {metadata.length - 2 > 0 && (
                    <span className="text-xs text-sidebar-foreground">
                        +{metadata.length - 2} more
                    </span>
                )}
            </div>
        );
    },
};

export const ASSOCIATED_NETWORKS_COLUMN: ColumnMetadata<
    PlatformTableData,
    PlatformTableDataColumns
> = {
    slug: "associated_networks",
    label: "Supported Networks",
    type: ColumnTypes.string,
    width: 225,
    maxWidth: 225,
    render: (platform) => {
        const allNetworks = platform.network_stats;

        if (allNetworks.length === 0) {
            return <Emdash />;
        }

        if (allNetworks.length === 1) {
            const network = allNetworks[0];

            return (
                <Link
                    className="py-0.5 px-0.5 flex items-center"
                    variant="default"
                    analyticsLabel={PlatformUserEvents.TableLinkClick}
                    analyticsEventProperties={{
                        network: network.slug,
                    }}
                    href={getNetworkURL({ networkSlug: network.slug })}
                >
                    <NetworkBadge
                        network={{
                            slug: network.slug,
                            name: network.name,
                            icon_url: network.icon_url,
                        }}
                        enableTooltip={false}
                    />
                    <p className="ml-2 dg-overflow">{network.name}</p>
                </Link>
            );
        }

        const visibleNetworks = allNetworks.slice(0, 10);
        const remainingNetworks = allNetworks.slice(10);
        const hasMore = remainingNetworks.length > 0;

        return (
            <div className="py-0.5 px-0.5 isolate flex -space-x-2 overflow-hidden h-full items-center w-full">
                {visibleNetworks.map((n) => (
                    <Link
                        key={n.slug}
                        href={getNetworkURL({ networkSlug: n.slug })}
                        analyticsLabel={PlatformUserEvents.TableLinkClick}
                        analyticsEventProperties={{
                            network: n.slug,
                        }}
                    >
                        <NetworkBadge
                            network={{
                                slug: n.slug,
                                name: n.name,
                                icon_url: n.icon_url,
                            }}
                        />
                    </Link>
                ))}
                {hasMore && (
                    <Tooltip
                        placement="top"
                        tooltipContent={
                            <div className="px-2 py-2">
                                <ul className="flex flex-col gap-1.5">
                                    {remainingNetworks.map((n) => (
                                        <li
                                            key={n.slug}
                                            className="whitespace-nowrap text-xs flex items-center gap-2"
                                        >
                                            <ImageLoader
                                                src={
                                                    n.icon_url || RWA_COIN_ICON
                                                }
                                                alt={`${n.name} Logo`}
                                                fallbackSrc={RWA_COIN_ICON}
                                                className="h-4 w-4 min-w-4 rounded-full"
                                            />
                                            {n.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                    >
                        {({ setTriggerRef }) => (
                            <span
                                ref={setTriggerRef}
                                className="text-xs text-sidebar-foreground ml-4 cursor-help"
                            >
                                +{remainingNetworks.length} more
                            </span>
                        )}
                    </Tooltip>
                )}
            </div>
        );
    },
};
